import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Alert,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MessageIcon from '@mui/icons-material/Message';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from '../components/DataTable';
import StatCard from '../components/StatCard';
import { useAdminApi } from '../hooks/useAdminApi';
import { OrganizationListResponse, OrganizationSummary } from '../types/adminApiTypes';
import { formatNumber } from '../utils/helpers';

// Define columns for the DataGrid
const columns: GridColDef<OrganizationSummary>[] = [
  {
    field: 'organization',
    headerName: 'Organization',
    flex: 1,
    renderCell: params => (
      // Link to the detail view, URL-encode the org name
      <MuiLink
        component={RouterLink}
        to={`/dashboard/organizations/${encodeURIComponent(params.value)}`}
      >
        {params.value}
      </MuiLink>
    ),
  },
  {
    field: 'total_users',
    headerName: 'Users',
    type: 'number',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => formatNumber(value),
  },
  {
    field: 'total_costs',
    headerName: 'Costs',
    type: 'number',
    width: 140,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => {
      // Format as currency
      const numValue = value as number;
      if (numValue === null || numValue === undefined) return '$0.00';
      return `$${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    field: 'total_user_messages',
    headerName: 'User Messages',
    type: 'number',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => formatNumber(value),
  },
  {
    field: 'total_chats',
    headerName: 'Chats',
    type: 'number',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => formatNumber(value),
  },
  {
    field: 'total_follow_ups',
    headerName: 'Follow-ups',
    type: 'number',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: value => formatNumber(value),
  },
];

const OrganizationsListView: React.FC = () => {
  const { data, loading, error, fetchData } = useAdminApi<OrganizationListResponse>();
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    fetchData({ endpoint: '/organizations' });
  }, [fetchData]);

  // Client-side filtering of organizations based on search term
  const filteredOrganizations = useMemo(() => {
    if (!data?.organizations) return [];
    if (!searchTerm.trim()) return data.organizations;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return data.organizations.filter(org =>
      org.organization.toLowerCase().includes(lowerSearchTerm)
    );
  }, [data?.organizations, searchTerm]);

  if (error) {
    return <Alert severity="error">Error loading organizations data: {error}</Alert>;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom component="div">
        Organizations Overview
      </Typography>

      {/* Summary Tiles Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Organizations"
            value={data?.summary?.total_organizations}
            icon={<BusinessIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Costs"
            value={data?.summary?.total_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total User Messages"
            value={data?.summary?.total_user_messages}
            icon={<MessageIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Search Bar Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ width: { xs: '100%', sm: '300px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Organizations Data Table */}
      <DataTable
        rows={filteredOrganizations}
        columns={columns}
        loading={loading}
        getRowId={row => row.organization} // Use organization name as unique ID
        // Pagination/Sorting are client-side by default for this endpoint
      />
    </Box>
  );
};

export default OrganizationsListView;
